/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import {
  FoodmaxapipbMenuV3ItemFilter,
  FoodmaxapipbEntityType,
} from "generated/foodmaxapi.type"
import { SectionData } from "types/api"
import { UpdateSellingTimeRequest } from "types/api/menuUpdateSellingTime"
import { UpdateDepartmentRequest } from "types/api/menuUpdateDepartment"
import { CreateSellingTimeRequest } from "types/api/menuCreateSellingTime"
import { ItemAttributeValue } from "types/models/item_attribute"
import {
  Item,
  BCRSInfo,
  PostPredictionFeedbacksGrabCategory,
} from "types/models/menu"
import { EnhanceImageTask } from "store/duck/menu.type"
import { GetErrorNotificationContent } from "./apiClient"

export enum HttpMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
}

// Base type for merchant context
export interface MerchantContext {
  merchantID?: string
  merchantGroupID?: string
}

// Request types for Menu V3 API
export interface MenuV3ModifierGroupsRequest extends MerchantContext {
  limit?: number
  pageIndex?: number
}

export interface MenuV3HierarchiesRequest extends MerchantContext {
  limit?: number
  pageIndex?: number
  parentType?: FoodmaxapipbEntityType
  parentID?: string
}

export interface MenuV3ItemsRequest extends MerchantContext {
  limit?: number
  pageIndex?: number
  parentType?: FoodmaxapipbEntityType
  parentID?: string
  filter?: Partial<FoodmaxapipbMenuV3ItemFilter>
}

export type MenuV3EditorStatusRequest = MerchantContext

// Request type for Menu V2 API (example)
export type MenuV2Request = MerchantContext

// Request type for Selling Times API
export interface ListSellingTimesRequest extends MerchantContext {
  isActive?: boolean
  page?: number
  pageSize?: number
  name?: string
}

// Request type for fetching modifier groups by IDs
export interface GetModifierGroupsByIDsRequest extends MerchantContext {
  modifierGroupIDs: string[]
}

// Request type for fetching items by IDs (GetItemDetailsByItemIDs endpoint)
export interface GetItemDetailsByItemIDsRequest extends MerchantContext {
  itemIDs: string[]
  needStock?: boolean
  needModifierGroups?: boolean
}

// Request type for search items (SearchItems endpoint)
export interface SearchItemsRequest extends MerchantContext {
  searchKey: string
  needClassItemsInfo?: boolean
  needCategoryInfo?: boolean
  needStock?: boolean
  needModifierGroups?: boolean
}

// Request type for search menus for mex (SearchMenusForMex endpoint)
export interface SearchMenusForMexRequest extends MerchantContext {
  keyword?: string
  entityType?: string
  departmentID?: string
}

// Request type for MartItemMenuDisplayV2 endpoint
export interface MartItemMenuDisplayV2Request extends MerchantContext {
  hideModifierGroup?: boolean
}

// Request type for the upload-file endpoint (POST /v2/upload-file)
export interface UploadFileRequest {
  merchantID: string
  file: {
    data: string // base64-encoded image data (without the data:mime;base64, prefix)
    type: string // mime sub-type, e.g. "jpeg", "png"
  }
  category: string // e.g. "menu_item_img"
}

// Request type for uploadItemPhoto — routes to /v2/upload-file or /v1/upload-menu-group-file
export interface UploadItemPhotoRequest extends MerchantContext {
  merchantID: string
  photo: string
  type: string
  menuGroupID?: string
}

// Response type for the upload-file endpoint
export interface UploadFileResponse {
  url: string
  metadata: {
    detection: {
      errorItems: string[] | null
      warningItems: string[] | null
    }
  }
}

// Request type for ListAttributes endpoint
export interface ListAttributesRequest extends MerchantContext {
  attributeClusterIDs: string[]
  page: number
  name?: string
  locale?: string
  isMenuGroup?: boolean
}

// Request type for fetchAttributeClusterOption endpoint
export interface FetchAttributeClusterOptionRequest extends MerchantContext {
  isMenuGroup: boolean
  menuGroupID?: string
  attributeClusterIDs: string
  page: number
  name?: string
  locale?: string
}

// Request type for FoodMaxAPI_UpdateItemsAvailableV1 (PUT /v1/items/available-status)
export interface UpdateItemsAvailableV1Request extends MerchantContext {
  availableStatus: number
  itemIDs?: string[]
  departmentIDs?: string[]
  subDepartmentIDs?: string[]
  excludedSubDepartmentIDs?: string[]
  excludedItemIDs?: string[]
}

// Request type for FoodMaxAPI_UpdateItemsSellingTimeV2 (PUT /v2/items/sellingTime)
export interface UpdateItemsSellingTimeRequest extends MerchantContext {
  menuGroupID?: string
  itemIDs?: string[]
  departmentIDs?: string[]
  subDepartmentIDs?: string[]
  excludedSubDepartmentIDs?: string[]
  excludedItemIDs?: string[]
  sellingTimeID: string
  showGlobalLoading?: boolean
  showError?: boolean
  showSuccess?: boolean
  successOptions?: { message?: string }
}

// Request type for DeleteItem (DELETE /v2/items/:itemID or /v1/menu-groups/items/:itemID)
export interface DeleteItemRequest extends MerchantContext {
  itemID: string
  menuGroupID?: string
  showGlobalLoading?: boolean
  getErrorNotificationContent?: GetErrorNotificationContent
}

// Request type for FoodMaxAPI_DeleteItemV2 (DELETE /v2/items/bulk)
export interface DeleteItemV2Request extends MerchantContext {
  itemIDs?: string[]
  categoryIDs?: string[]
  departmentIDs?: string[]
  subDepartmentIDs?: string[]
  excludedSubDepartmentIDs?: string[]
  excludedItemIDs?: string[]
}

// Request type for deleteMenuGroup (DELETE /v1/menu-groups/:menuGroupID/delete)
export interface DeleteMenuGroupRequest extends MerchantContext {
  menuGroupID: string
  merchantGroupID: string // required — overrides optional from MerchantContext
}

// Request type for fetchMerchantDetail endpoint (portal foodtroy v2)
export interface FetchMerchantDetailRequest extends MerchantContext {
  merchantID: string // required — overrides optional from MerchantContext
  countryCode: string
}

// Request type for uploadReportReviewImg (POST /v2/upload-file)
export interface UploadReportReviewImgRequest extends MerchantContext {
  merchantID: string // Override to make required
  data: string
  type: string
}

// Request type for fetchIncidentList (GET /v1/merchant/incidentlist)
export interface FetchIncidentListRequest extends MerchantContext {
  merchantID: string // Override to make required (old Redux action expects it)
  incidentType?: string[]
  resolutionType?: string[]
  fromTime?: string
  endTime?: string
  page?: number
  pageSize?: number
}

// Request type for FoodMaxAPI_FetchIncidentStat (GET /v1/merchant/incidentstats)
export interface FetchIncidentStatRequest extends MerchantContext {
  filterBy?: string
}

// Request type for FoodMaxAPI_GetIncident (GET /v1/merchant/incident)
export interface GetIncidentRequest extends MerchantContext {
  merchantID: string // Override to make required (old Redux action expects it)
  incidentID: string
}

// Request type for SearchOutlets endpoint (delvplatformapi)
export interface SearchOutletsRequest extends MerchantContext {
  search?: string
  offset?: number
  limit?: number
  includeItemsWithoutPhotosCount?: boolean
  city?: string[]
  asc?: boolean
  modelType?: string
  deliverOption?: string[]
  businessType?: number[]
  includeMenuGroupV2ID?: boolean
}

// Request type for SubmitReportReview (POST /v1/feedback/report-review/{reviewID})
export interface SubmitReportReviewRequest extends MerchantContext {
  reviewID: string
  reasons: string[]
  imageTokens: string[]
  imageUrls: string[]
  description: string
}

// Request type for PostMerchantReviewThrottle endpoint (POST /v1/feedback/merchant-review-throttles/:merchantID)
export interface PostMerchantReviewThrottleRequest extends MerchantContext {
  merchantID: string
}

// Request type for FetchMerchantAggregate (GET /v1/merchant-group/store/aggregate-by-fields)
export interface FetchMerchantAggregateRequest extends MerchantContext {
  fields: string[]
}

// Request type for FetchOrderEta (GET /v2/orders-eta)
export interface FetchOrderEtaRequest extends MerchantContext {
  merchantID: string // Override to make required
  orderIDs: string // Comma-separated order IDs
}

// Request type for FetchCityList (GET /v1/merchant-group/store/citylist)
export interface FetchCityListRequest extends MerchantContext {
  includeInactive?: boolean
}

// Request type for FetchOrderCancelReasons (GET /v1/order-unchangeable-reasons)
export interface FetchOrderCancelReasonsRequest extends MerchantContext {
  merchantID: string
  orderID: string
  locale: string
}

// Request type for FetchOrderSummary (GET /v1/merchant-report-summary)
export interface FetchOrderSummaryRequest extends MerchantContext {
  merchantID: string
  startDate: string
}

// Request type for ReviewOrderChanges (POST /v1/review-order-changes/:orderID)
export interface ReviewOrderChangesRtkRequest extends MerchantContext {
  orderID: string
  merchantID: string
  data: {
    orderID: string
    items: {
      itemID: string
      itemName: string
      quantity: number
      priceInMin: number
      status: number
      modifierGroups: {
        modifierGroupID: string
        modifierGroupName: string
        modifiers: {
          modifierID: string
          modifierName: string
          quantity: number
          priceInMin: number
          status: number
        }[]
      }[]
      adjustedWeight: number
      originalIndex: number
    }[]
    isSkipOOSCheck: boolean
  }
}

// Request type for SubmitOrderChanges (PUT /v1/submit-order-changes/:orderID)
export interface SubmitOrderChangesRequest extends MerchantContext {
  merchantID: string
  orderID: string
  orderSignatureID: string
}

// Request type for CancelOrder (PUT /v1/cancel-order/:orderID)
export interface CancelOrderRequest extends MerchantContext {
  orderID: string
  cancelReason: string
  cancelCode: number
}

// Request type for FetchOrderExportedMpaFile (POST /v1/mexsupplies/mpatransaction/export)
export interface FetchOrderExportedMpaFileRequest extends MerchantContext {
  merchantID: string // Override to make required
  startTime: string
  endTime: string
}

// Response type for FetchOrderExportedMpaFile
export interface FetchOrderExportedMpaFileResponse {
  downloadUrl: string
}

// Request type for DeleteCategory (DELETE /v2/categories/:categoryID or /v1/menu-groups/categories/:categoryID)
export interface DeleteCategoryRequest extends MerchantContext {
  merchantID: string
  categoryID: string
  menuGroupID?: string
  showGlobalLoading?: boolean
  showSuccess?: boolean
  successOptions?: { message?: string }
  showError?: boolean
}

// Request type for FetchOrderExportedFile (POST /v1/mexsupplies/invoice/export)
export interface FetchOrderExportedFileRequest extends MerchantContext {
  merchantID: string // Override to make required
  orderIDs: string[]
}

// Response type for FetchOrderExportedFile
export interface FetchOrderExportedFileResponse {
  downloadUrl: string
}

// Request type for FetchStoreDetail (GET /v2/merchants)
export interface FetchStoreDetailRequest extends MerchantContext {
  merchantID: string // Override to make required
}

// Request type for FetchStoreOpenStatus (GET /v3/open-status)
export interface FetchStoreOpenStatusRequest extends MerchantContext {
  merchantID: string // Override to make required
}

// Request type for CreateModifierGroup (POST /v2/modifier-groups or /v1/menu-groups/modifier-groups)
export interface CreateModifierGroupRequest extends MerchantContext {
  modifierGroup: {
    selectionRangeMin: number
    selectionRangeMax: number
    // FMQ (Flexible Modifier Quantity) config — BP-48453
    isFlexibleQuantityEnabled?: boolean
    maxModifierSelectionQuantity?: number
    modifierGroupName: string
    nameTranslation: any
    modifiers: {
      advancedPricing: any
      priceInMin: number
      modifierID: string
      modifierName: string
      nameTranslation: any
      bcrsInfo?: BCRSInfo
    }[]
    modifierGroupID: string
  }
  menuGroupID?: string
  currency?: string
}

// Request type for UpdateStoreStatus (PUT /v1/merchant/status)
export interface UpdateStoreStatusRequest extends MerchantContext {
  merchantID: string // Override to make required
  params: {
    fromState: string
    toState: string
    busyModeRequest: {
      busyModeStartTime?: string | null
      busyModeFoodPrepareTime?: number | null
      option?: number | null
    }
    tempPauseRequest: {
      isUnpause?: boolean | null
      tempPauseEnd?: string | null
    }
  }
}

// Request type for MarkOrder (POST /orders/mark, /v1/deliver-by-mex, /v3/orders/:orderID/terminate)
export interface MarkOrderRequest extends MerchantContext {
  merchantID: string
  data: MarkOrderBody | MarkOrderDeliverBody
  status?: number
}

export interface MarkOrderBody {
  orderIDs: string[]
  markStatus: number
  preparationTaskIDs?: string[]
}

export interface MarkOrderDeliverBody {
  orderID: string
  mexSupplierCourierInfo?: {
    courierName?: string
    trackingNumber?: string
  }
}

// Request type for FetchOrderByID (GET /v3/orders/:orderID)
export interface FetchOrderByIDRequest extends MerchantContext {
  orderID: string
  merchantID: string
}

// Request type for DeleteModifierGroup (DELETE /v2/modifier-groups/:id or /v1/menu-groups/modifier-groups/:id)
export interface DeleteModifierGroupRequest extends MerchantContext {
  modifierGroupID: string
  menuGroupID?: string
}

// Request type for ChangeModifierAvailability (PUT /v2/modifiers/available)
export interface ChangeModifierAvailabilityRequest extends MerchantContext {
  merchantID: string
  availableStatus: number
  modifierIDs: string[]
}

// Request type for fetchMenuGroupList (GET /v1/menu-groups)
export interface FetchMenuGroupListRequest extends MerchantContext {
  menuGroupName?: string
  isWithItemPhotoCount?: boolean
}

// Request type for CreateMenuGroup (POST /v1/menu-groups)
export interface CreateMenuGroupRequest extends MerchantContext {
  merchantGroupID: string
  menuGroup: {
    name: string
    businessType: number
    menuType: 0 | 1
    timeZone: string
  }
  menuGroupID?: string
  linkedStoreIDs?: string[]
}

// Request type for UpsertItemV2 (POST /v2/upsert-item or /v2/menu-groups/upsert-item)
export interface UpsertItemV2RtkRequest extends MerchantContext {
  menuGroupID?: string
  item: Item
  categoryID: string
  itemAttributeValues?: ItemAttributeValue[]
  getErrorNotificationContent?: GetErrorNotificationContent
}

// Request type for LinkItemToModifierGroup (POST /v2/item-modifier-groups or /v1/menu-groups/item-modifier-groups)
export interface LinkItemToModifierGroupRequest extends MerchantContext {
  merchantID: string
  modifierGroupID: string
  itemID: string
  menuGroupID?: string
  showError?: boolean
}

// Request type for UnLinkItemFromModifierGroup (DELETE /v2/item-modifier-groups or /v1/menu-groups/item-modifier-groups)
export interface UnLinkItemFromModifierGroupRequest extends MerchantContext {
  merchantID: string
  modifierGroupID: string
  itemID: string
  menuGroupID?: string
  showError?: boolean
  getErrorNotificationContent?: GetErrorNotificationContent
}

// Request type for GetTranslation (POST /v1/menu-translations or /v1/menu-groups/menu-translations)
export interface GetTranslationRequest extends MerchantContext {
  merchantID: string
  merchantGroupID: string
  textType: string
  entity: string
  text: string
  textSourceLang: string
  textTargetLang: string[]
  destinationKey?: string
  menuGroupID?: string
}

// Request type for UpdateExistingTranslation (POST /v1/menu-translations or /v1/menu-groups/menu-translations)
export interface UpdateExistingTranslationRequest extends MerchantContext {
  merchantID: string
  merchantGroupID: string
  textType: "name" | "description"
  entity: "category" | "item" | "modifierGroup" | "modifier"
  text: string
  textSourceLang: string
  textTargetLang: string[]
  destinationKey?: string
  menuGroupID?: string
}

// Request type for ResetMenuGroup (PUT /v1/menu-groups/:menuGroupID/reset)
export interface ResetMenuGroupRequest extends MerchantContext {
  merchantGroupID: string
  menuGroupID: string
}

// Request type for EditModifierGroup (PUT /v2/modifier-groups/:id or /v1/menu-groups/modifier-groups/:id)
export interface EditModifierGroupRequest extends MerchantContext {
  modifierGroupID: string
  merchantID: string
  menuGroupID?: string
  currency?: string
  modifierGroup: {
    modifierGroupID: string
    selectionRangeMin: number
    selectionRangeMax: number
    // FMQ (Flexible Modifier Quantity) config — BP-48453
    isFlexibleQuantityEnabled?: boolean
    maxModifierSelectionQuantity?: number
    modifierGroupName: string
    nameTranslation?: any
    modifiers?: {
      advancedPricing?: any
      priceInMin: number
      modifierID: string
      modifierName: string
      nameTranslation?: any
      sortOrder?: number
      availableStatus?: number
      bcrsInfo?: any
    }[]
  }
}

// Request type for FetchInventoryExportedFile (POST /v1/{inventory/categories|items}/export)
export interface FetchInventoryExportedFileRequest extends MerchantContext {
  merchantID: string
  merchantGroupID: string
  bulkUploadType: string
  categoryIDs?: string[]
  menuGroupID?: string
  itemStatus?: string
  properties?: string[]
  templateType?: string
  includeBcrsColumn?: boolean
  isForBcrsRecommendation?: boolean
}

// Request type for fetchInventoryCategory (GET /v1/{inventory|menu}/categories)
export interface FetchInventoryCategoryRequest extends MerchantContext {
  categoryName: string
  menuGroupID?: string
  bulkUploadType: string
}

// Request type for FetchDepartmentsForGrabCategory
export interface FetchDepartmentsForGrabCategoryRequest
  extends MerchantContext {
  searchName?: string
  locale?: string
  menuGroupID?: string
}

// Request type for FetchSubdepartmentsForGrabCategory
// GET /v2/item-classes or /v1/menu-groups/item-classes
export interface FetchSubdepartmentsForGrabCategoryRequest
  extends MerchantContext {
  parentID: string
  locale?: string
  menuGroupID?: string
}

// Request type for GetVerifyTranslation (POST /v1/verify-translation or /v1/menu-groups/verify-translation)
export interface GetVerifyTranslationRequest extends MerchantContext {
  merchantID: string
  merchantGroupID: string
  entityType: string
  entityID?: string
  nameTranslation?: Record<string, string>
  descriptionTranslation?: Record<string, string>
  menuGroupID?: string
}

// Request type for ProofModifierGroup (POST /v2/modifier-groups/:id/proof or /v1/menu-groups/modifier-groups/:id/proof)
export interface ProofModifierGroupRequest extends MerchantContext {
  modifierGroupID: string
  menuGroupID?: string
  showError?: boolean
}

export interface PublishMenuGroupRequest extends MerchantContext {
  merchantGroupID: string
  menuGroupID: string
  linkedStoreIDs: string[]
}

// Request type for SortDepartments (PUT /departments-sort or /v1/menu-groups/departments-sort)
export interface SortDepartmentsRequest extends MerchantContext {
  merchantID: string
  sorts: { resourceID: string; sortOrder: number }[]
  parentDepartmentID?: string | null
  menuGroupID?: string
  showError?: boolean
  showSuccess?: boolean
  successOptions?: { message?: string }
}

// Request type for SortItems (PUT /items-sort or /v1/menu-groups/items-sort)
export interface SortItemsRequest extends MerchantContext {
  merchantID: string
  categoryID: string
  sorts: { resourceID: string; sortOrder: number }[]
  menuGroupID?: string
  showError?: boolean
  showSuccess?: boolean
  successOptions?: { message?: string }
}

// Request type for SortCategories (PUT /categories-sort or /v1/menu-groups/categories-sort)
export interface SortCategoriesRequest extends MerchantContext {
  sectionID: string
  sorts: { resourceID: string; sortOrder: number }[]
  menuGroupID?: string
  showError?: boolean
  showSuccess?: boolean
  successOptions?: { message?: string }
}

// Request type for CreateCategory (POST /v2/categories or /v1/menu-groups/categories)
export interface CreateCategoryRtkRequest extends MerchantContext {
  menuGroupID?: string
  data: {
    name: string
    categoryID: string
    sectionID: string
    sellingTimeID?: string
    nameTranslation?: any
  }
  getErrorNotificationContent?: GetErrorNotificationContent
  showSuccess?: boolean
  successOptions?: { message?: string; description?: string }
}

// Request type for LinkStoreToMenuGroup (PUT /v1/menu-groups/:menuGroupID/link)
export interface LinkStoreToMenuGroupRequest extends MerchantContext {
  merchantGroupID: string
  menuGroupID: string
  storeIDs: string[]
  getErrorNotificationContent?: GetErrorNotificationContent
}

// Request type for CreateSection (POST /v1/section or /v1/menu-groups/section)
export interface CreateSectionRtkRequest extends MerchantContext {
  menuGroupID?: string
  sectionToCreate?: SectionData
  sectionsToUpdate?: SectionData[]
  getErrorNotificationContent?: GetErrorNotificationContent
  showSuccess?: boolean
  successOptions?: { message?: string }
}

// Request type for DeleteSection (DELETE /v1/section or /v1/menu-groups/section)
export interface DeleteSectionRequest extends MerchantContext {
  merchantID: string
  sectionID: string
  menuGroupID?: string
  showSuccess?: boolean
  successOptions?: { message?: string }
}

// Request type for UnlinkStoreFromMenuGroup (PUT /v1/menu-groups/:menuGroupID/unlink)
export interface UnlinkStoreFromMenuGroupRequest extends MerchantContext {
  merchantGroupID: string
  menuGroupID: string
  storeIDs: string[]
}

// Request type for SortDepartmentItems (PUT /departments/items-sort or /v1/menu-groups/departments/items-sort)
export interface SortDepartmentItemsRequest extends MerchantContext {
  departmentID: string
  sorts: { resourceID: string; sortOrder: number }[]
  menuGroupID?: string
  showError?: boolean
  showSuccess?: boolean
  successOptions?: { message?: string }
}

// Request type for PostFeedbackGrabCategory (POST /v1/prediction-feedbacks or /v1/menu/prediction-feedbacks)
export interface PostFeedbackGrabCategoryRequest extends MerchantContext {
  merchantID: string
  merchantGroupID: string
  menuGroupID?: string
  predictionFeedback: PostPredictionFeedbacksGrabCategory
}
// Request type for FetchSupportedAttributeCluster
// GET /v1/supported-attribute-clusters or /v2/menu-groups/supported-attribute-clusters
export interface FetchSupportedAttributeClusterRequest extends MerchantContext {
  isMenuGroup: boolean
  menuGroupID?: string
  itemClassIDs: string
  countryID: number
  businessType: string
  itemID?: string
}

export interface FetchMenuGroupLinkedStoresRequest {
  menuGroupID: string
  merchantGroupID: string
  page: number
  pageSize: number
}

export interface FetchStoreListRequest {
  countryCode: string
  offset: number
  limit: number
  isWithItemPhotoCount?: boolean
}

// Request type for UpdateSellingTime (PUT /v1/menu/selling-times/:sellingTimeID or /v1/menu-groups/selling-times/:sellingTimeID)
export interface UpdateSellingTimeRtkRequest extends MerchantContext {
  sellingTimeID: string
  data: UpdateSellingTimeRequest
  menuGroupID?: string
  getErrorNotificationContent?: GetErrorNotificationContent
  showSuccess?: boolean
  successOptions?: { message?: string }
}

// Request type for UpdateDepartment (PUT /v1/departments/:departmentID or /v1/menu-groups/departments/:departmentID)
export interface UpdateDepartmentRtkRequest extends MerchantContext {
  departmentID: string
  menuGroupID?: string
  data: UpdateDepartmentRequest
  getErrorNotificationContent?: GetErrorNotificationContent
  showError?: boolean
  showSuccess?: boolean
  successOptions?: { message?: string }
}

// Request type for UpdateMenuGroup (PUT /v1/menu-groups/:menuGroupID)
export interface UpdateMenuGroupRequest extends MerchantContext {
  merchantGroupID: string
  menuGroupID: string
  menuGroupName: string
}

export interface CreateSellingTimeRtkRequest extends MerchantContext {
  menuGroupID?: string
  showError?: boolean
  getErrorNotificationContent?: GetErrorNotificationContent
  showSuccess?: boolean
  successOptions?: { message?: string }
  data: CreateSellingTimeRequest
}

// Request type for DeleteSellingTime (DELETE /v1/menu/selling-times/:sellingTimeID or /v1/menu-groups/selling-times/:sellingTimeID)
export interface DeleteSellingTimeRequest extends MerchantContext {
  sellingTimeID: string
  menuGroupID?: string
  showError?: boolean
  getErrorNotificationContent?: GetErrorNotificationContent
}
export interface UpdateCategoryRtkRequest extends MerchantContext {
  categoryID: string
  data: {
    name: string
    categoryID: string
    sectionID: string
    sellingTimeID: string
    nameTranslation?: any
  }
  isSellingTimeStructure: boolean
  menuGroupID?: string
  getErrorNotificationContent?: GetErrorNotificationContent
  showSuccess?: boolean
  successOptions?: { message?: string }
}

// Request type for UpdateSections (PUT /v1/sections or /v1/menu-groups/sections)
export interface UpdateSectionsRequest extends MerchantContext {
  sections: SectionData[]
  menuGroupID?: string
  showError?: boolean
  showSuccess?: boolean
  successOptions?: { message?: string }
  getErrorNotificationContent?: GetErrorNotificationContent
}

export interface EnhanceImageRequest extends MerchantContext {
  image: string
  task: EnhanceImageTask
}

export interface FetchListBrandsSearchRequest extends MerchantContext {
  merchantID: string
  locale: string
  page: number
  name: string
}

export interface LoadMoreDepartmentsForGrabCategoryRequest
  extends MerchantContext {
  menuGroupID?: string
  searchName?: string
  locale?: string
  offsetGrabCategory?: number
  isLoadingMore?: boolean
}
